import { EntityNames } from 'src/graphql/types';
import { create, StateCreator } from 'zustand';
import { createSelectors } from './utils';

export type BulkUpdateOperationKind = 'update' | 'delete';

export interface BulkUpdateJobState {
  jobId: string;
  entityName: EntityNames;
  viewId: string;
  partnerId: number;
  operationKind: BulkUpdateOperationKind;
  status: string | null;
  dismissed: boolean;
  isUndoInProgress: boolean;
  hasTerminalSyncApplied: boolean;
}

export interface BulkUpdateJobsState {
  activeBulkUpdateJob: BulkUpdateJobState | null;
  bulkUpdateSourceViewContextKey: string | null;
  pendingBulkUpdateRefreshContextKeys: string[];
  startBulkUpdateJob: (input: {
    jobId: string;
    entityName: EntityNames;
    viewId: string;
    partnerId: number;
    operationKind: BulkUpdateOperationKind;
  }) => void;
  updateBulkUpdateJob: (
    jobId: string,
    patch: Partial<
      Pick<
        BulkUpdateJobState,
        'status' | 'dismissed' | 'isUndoInProgress' | 'hasTerminalSyncApplied'
      >
    >,
  ) => void;
  clearBulkUpdateJob: (jobId?: string) => void;
  dismissBulkUpdateJob: (jobId: string) => void;
  setBulkUpdateSourceViewContextKey: (contextKey: string | null) => void;
  markBulkUpdateViewForRefresh: (contextKey: string) => void;
  clearBulkUpdateViewRefreshFlag: (contextKey: string) => void;
}

export function getBulkUpdateViewContextKey(entityName: EntityNames, viewId: string): string {
  return `${entityName}:${viewId}`;
}

// `selectedPartnerId === null` means multi-tenancy mode: the portal omits
// the `ov-partner-ids` header so the request scope falls back to the user's
// full `available_partner_ids`. Otherwise the request is pinned to the
// selected partner and only jobs from that partner are pollable.
export function isBulkUpdateJobInPartnerScope(
  job: Pick<BulkUpdateJobState, 'partnerId'> | null,
  selectedPartnerId: number | null,
): boolean {
  if (!job) {
    return false;
  }

  return selectedPartnerId === null || job.partnerId === selectedPartnerId;
}

export const createBulkUpdateJobsSlice: StateCreator<
  BulkUpdateJobsState,
  [],
  [],
  BulkUpdateJobsState
> = (set) => ({
  activeBulkUpdateJob: null,
  bulkUpdateSourceViewContextKey: null,
  pendingBulkUpdateRefreshContextKeys: [],
  startBulkUpdateJob: ({ jobId, entityName, viewId, partnerId, operationKind }) => {
    set({
      activeBulkUpdateJob: {
        jobId,
        entityName,
        viewId,
        partnerId,
        operationKind,
        status: null,
        dismissed: false,
        isUndoInProgress: false,
        hasTerminalSyncApplied: false,
      },
    });
  },
  updateBulkUpdateJob: (jobId, patch) =>
    set((state) => {
      if (!state.activeBulkUpdateJob || state.activeBulkUpdateJob.jobId !== jobId) {
        return state;
      }

      return {
        activeBulkUpdateJob: {
          ...state.activeBulkUpdateJob,
          ...patch,
        },
      };
    }),
  clearBulkUpdateJob: (jobId) =>
    set((state) => {
      if (!state.activeBulkUpdateJob) {
        return state;
      }
      if (jobId && state.activeBulkUpdateJob.jobId !== jobId) {
        return state;
      }

      return {
        activeBulkUpdateJob: null,
      };
    }),
  dismissBulkUpdateJob: (jobId) =>
    set((state) => {
      if (!state.activeBulkUpdateJob || state.activeBulkUpdateJob.jobId !== jobId) {
        return state;
      }
      return {
        activeBulkUpdateJob: {
          ...state.activeBulkUpdateJob,
          dismissed: true,
        },
      };
    }),
  setBulkUpdateSourceViewContextKey: (contextKey) =>
    set({
      bulkUpdateSourceViewContextKey: contextKey,
    }),
  markBulkUpdateViewForRefresh: (contextKey) =>
    set((state) => {
      if (state.pendingBulkUpdateRefreshContextKeys.includes(contextKey)) {
        return state;
      }

      return {
        pendingBulkUpdateRefreshContextKeys: [
          ...state.pendingBulkUpdateRefreshContextKeys,
          contextKey,
        ],
      };
    }),
  clearBulkUpdateViewRefreshFlag: (contextKey) =>
    set((state) => ({
      pendingBulkUpdateRefreshContextKeys: state.pendingBulkUpdateRefreshContextKeys.filter(
        (key) => key !== contextKey,
      ),
    })),
});

const useBulkUpdateJobsStoreBase = create<BulkUpdateJobsState>()((...args) => ({
  ...createBulkUpdateJobsSlice(...args),
}));

export const useBulkUpdateJobsStore = createSelectors(useBulkUpdateJobsStoreBase);
